'use client';

import {
  createTranslator,
  IntlErrorCode,
  Messages,
  NamespaceKeys,
  NestedKeyOf,
  useLocale,
  useTranslations,
} from 'next-intl';
import { useCallback } from 'react';

import { FE_LANG } from '@/constants/common';
import { defaultNamespace, I18nNamespaces, namespaces } from '@/i18n/settings';

export const useNextTranslation = (namespace?: I18nNamespaces) => {
  const t = useTranslations(namespace);
  return { t };
};

export const useLanguage = () => {
  const lng = useLocale() as FE_LANG;
  return { lng };
};

type Options = Record<string, string | number | Date>;

export const useTranslation = (_currentLang: string = '', ns: string = defaultNamespace) => {
  const t = useTranslations();
  const compatibleT = useCallback(
    (key: string = '', options?: unknown) => {
      if (typeof key !== 'string') {
        return '';
      }

      const compatibleKey = key.replace(':', '.');
      const keyList = compatibleKey.split('.') as I18nNamespaces[];

      if (!namespaces.includes(keyList[0]) || keyList.length <= 1 /* because of city.city */) {
        keyList.unshift(ns as I18nNamespaces);
      }
      const finalKey = keyList.join('.');
      if (t.has(finalKey)) {
        return t(finalKey, options as Options);
      } else {
        keyList[0] = defaultNamespace;
        const fallbackKey = keyList.join('.');
        return t.has(fallbackKey) ? t(fallbackKey, options as Options) : key;
      }
    },
    [ns, t],
  );
  return {
    t: compatibleT,
  };
};

export type NextTFunction = ReturnType<
  typeof createTranslator<Messages, NamespaceKeys<Messages, NestedKeyOf<Messages>>>
>;

export const mockNextTFunction: NextTFunction = Object.assign(
  ((_key: string, ..._args: unknown[]) => '') as NextTFunction,
  {
    has: (_key: string) => true,
    rich: (key: string, ..._args: unknown[]) => `rich:${key}` as unknown as React.ReactNode,
    raw: (key: string, ..._args: unknown[]) => `raw:${key}`,
    markup: (key: string, ..._args: unknown[]) => `markup:${key}`,
  },
);

export const findTranslatedText = (t: NextTFunction, key: string) => {
  for (const ns of namespaces) {
    const combinedKey = `${ns}.${key}`;
    if (t.has(combinedKey)) {
      return t(combinedKey);
    }
  }
  return key;
};

export const safeTranslate = (t: NextTFunction, key: string, fallback?: string) => {
  if (t.has(key)) {
    return t(key);
  }
  return fallback ?? key; // It is expected to not be found, and it should not show a warning.
};

export const initI18nextExcel = (lng: FE_LANG) => {
  const en = createTranslator({ locale: FE_LANG.EN_US, messages: createMessages(FE_LANG.EN_US) });
  return createTranslator({
    locale: lng,
    messages: createMessages(lng),
    getMessageFallback: ({ key, error }) => {
      if (error.code === IntlErrorCode.MISSING_MESSAGE) {
        return en(key as never);
      }
      return key;
    },
  });
};
export const createMessages = (lng: FE_LANG) => {
  const messages: Record<string, unknown> = {};
  for (const ns of namespaces) {
    try {
      messages[ns] = require(`@/i18n/locales/${lng}/${ns}`);
    } catch {
      // some files can't found zh-CN/** without using in the template
    }
  }
  return messages;
};
